import { Controller } from "@hotwired/stimulus"
import "emoji-picker-element"

export default class extends Controller {
  static targets = ["list", "chip", "hidden", "dropdown", "labels", "labelRow", "labelTemplate"]
  static values = { labelName: String }

  connect() {
    this.dropdownTarget.querySelector("emoji-picker")
      .addEventListener("emoji-click", (e) => this.onEmojiClick(e))
  }

  toggle(event) {
    event.stopPropagation()
    this.dropdownTarget.classList.toggle("hidden")
  }

  closeOnOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.dropdownTarget.classList.add("hidden")
    }
  }

  onEmojiClick(event) {
    this.addChip(event.detail.unicode)
    this.dropdownTarget.classList.add("hidden")
  }

  remove(event) {
    event.currentTarget.closest('[data-emoji-chip-picker-target="chip"]').remove()
    this.syncHidden()
  }

  dragStart(event) {
    this.dragging = event.currentTarget
    event.dataTransfer.effectAllowed = "move"
  }

  dragOver(event) {
    event.preventDefault()
    const target = event.currentTarget
    if (!this.dragging || target === this.dragging) return

    const rect = target.getBoundingClientRect()
    const before = event.clientX - rect.left < rect.width / 2
    this.listTarget.insertBefore(this.dragging, before ? target : target.nextSibling)
  }

  drop(event) {
    event.preventDefault()
  }

  dragEnd() {
    this.dragging = null
    this.syncHidden()
  }

  addChip(emoji) {
    if (this.chipTargets.some((chip) => this.chipEmoji(chip) === emoji)) return

    this.listTarget.appendChild(this.buildChip(emoji))
    this.syncHidden()
  }

  buildChip(emoji) {
    const li = document.createElement("li")
    li.className = this.chipTargets[0]?.className || "badge badge-lg gap-1 cursor-move"
    li.draggable = true
    li.dataset.emojiChipPickerTarget = "chip"
    li.dataset.action = "dragstart->emoji-chip-picker#dragStart dragover->emoji-chip-picker#dragOver drop->emoji-chip-picker#drop dragend->emoji-chip-picker#dragEnd"

    const span = document.createElement("span")
    span.textContent = emoji
    li.appendChild(span)

    const button = document.createElement("button")
    button.type = "button"
    button.textContent = "×"
    button.className = "leading-none text-base-content/50 hover:text-base-content"
    button.dataset.action = "click->emoji-chip-picker#remove"
    li.appendChild(button)

    return li
  }

  chipEmoji(chip) {
    return chip.querySelector("span").textContent
  }

  syncHidden() {
    const emojis = this.chipTargets.map((chip) => this.chipEmoji(chip))
    this.hiddenTarget.value = emojis.join("|")
    this.syncLabels(emojis)
  }

  syncLabels(emojis) {
    const rows = new Map(this.labelRowTargets.map((row) => [row.dataset.emoji, row]))

    emojis.forEach((emoji) => {
      const row = rows.get(emoji) || this.buildLabelRow(emoji)
      rows.delete(emoji)
      this.labelsTarget.appendChild(row)
    })
    rows.forEach((row) => row.remove())

    this.labelsTarget.hidden = emojis.length === 0
  }

  buildLabelRow(emoji) {
    const row = this.labelTemplateTarget.content.firstElementChild.cloneNode(true)
    row.dataset.emoji = emoji
    row.querySelector("[data-emoji-label-display]").textContent = emoji
    row.querySelector("input").name = `${this.labelNameValue}[${emoji}]`
    return row
  }
}
